// src/context/AuthContext.js
//
// Authentication context for the Synvex Business Management System.
// Stores the logged-in user's info (id/email/role, or employee_id/role
// for employee accounts) and the JWT access token.
// Persists to localStorage so the session survives a page refresh.

import React, { createContext, useState, useContext, useEffect } from 'react';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // On first load, restore session from localStorage (if present)
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const normalizedUser = {
          ...parsedUser,
          user_id: parsedUser.user_id || parsedUser.id,
        };
        setToken(storedToken);
        setUser(normalizedUser);
      } catch (err) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      }
    }
    setLoading(false);
  }, []);

  // userData shape: { id or employee_id, full_name, email, role }
  // tokens shape: { access_token, refresh_token }
  const login = (userData, tokens) => {
    const normalizedUser = {
      ...userData,
      user_id: userData.user_id || userData.id,
    };
    setUser(normalizedUser);
    setToken(tokens.access_token);

    localStorage.setItem('access_token', tokens.access_token);
    if (tokens.refresh_token) {
      localStorage.setItem('refresh_token', tokens.refresh_token);
    }
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);